import {
  ChevronDown,
  Download,
  FileText,
  History,
  Loader2,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { ConfirmPopover } from '~/components/common/confirm-popover';
import { Button } from '~/components/ui/button';
import { DatePicker } from '~/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';
import { formatDate, formatDateTime, formatRelative } from '~/utils/date';

import { useExportDownload } from '../hooks/use-export-download';
import {
  type ExportCreatedWindow,
  useDeleteExport,
  WINDOWED_LIST_LIMIT,
  useExportsList,
} from '../hooks/use-export-mutations';
import type { ExportListItem, ExportStatus } from '../types/export.types';
import { isTerminalExportStatus } from '../types/export.types';

import { ExportConfigDetails } from './export-config-details';

interface ExportHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Scope the list to one enrichment run's exports. */
  sourceWorkflowId: string;
}

/** The created-at ranges the history offers; `custom` opens two date pickers. */
const RANGE_PRESETS = ['all', '7', '30', '90', 'custom'] as const;
type RangePreset = (typeof RANGE_PRESETS)[number];

/** Local midnight of *date*, shifted by *days* — day boundaries are the viewer's, not UTC's. */
function startOfLocalDay(date: Date, days = 0): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** The window for a "last N days" preset: from local midnight N-1 days ago, open-ended. */
function presetCreatedWindow(preset: RangePreset): ExportCreatedWindow {
  if (preset === 'all' || preset === 'custom') return {};
  return { from: startOfLocalDay(new Date(), 1 - Number(preset)).toISOString() };
}

/** The window for two picked days, both inclusive (the server's `to` is exclusive). */
function customWindow(from: Date | undefined, to: Date | undefined): ExportCreatedWindow {
  return {
    ...(from ? { from: startOfLocalDay(from).toISOString() } : {}),
    ...(to ? { to: startOfLocalDay(to, 1).toISOString() } : {}),
  };
}

/** Map an export status to its indicator-dot colour token. */
function statusDotClass(status: ExportStatus): string {
  switch (status) {
    case 'COMPLETED':
      return 'bg-success';
    case 'FAILED':
    case 'CANCELLED':
      return 'bg-destructive';
    default:
      return 'bg-info';
  }
}

/** One export row — status, product count, created time, download + delete actions. */
function HistoryRow({ item }: { item: ExportListItem }) {
  const { t } = useTranslation('exports');
  const { download, isRefreshing } = useExportDownload();
  const deleteMutation = useDeleteExport();
  const isTerminal = isTerminalExportStatus(item.status);
  const createdAt = item.createdAt ?? null;
  // Delete is destructive and irreversible, so it's gated behind a confirm.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Non-terminal exports pulse their status dot to read as "still working".
  const isActive = !isTerminal;
  // "Configuration": what this export ran with, fetched only when opened.
  const [showConfig, setShowConfig] = useState(false);

  // Show the full file name in a tooltip only when it's actually clipped —
  // measured off the rendered node, re-checked on resize.
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.scrollWidth > el.clientWidth;
      setIsNameTruncated((prev) => (prev === next ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [item.fileName]);

  // Details line under the status: product count (unless it is the primary
  // line), language, layout and relative time, dot-separated. Without a file
  // name the layout, else the count, is promoted to the primary line instead.
  // The file layout it ran, e.g. "Hibbett · Catalog XML v3" (absent on the standard path).
  const layoutLabel = item.transformerName
    ? `${item.transformerName}${item.transformerVersion != null ? ` v${String(item.transformerVersion)}` : ''}`
    : null;
  // A transformed export's list item carries no file name, so its layout leads instead.
  const productCount = t('history.productCount', { count: item.writtenProducts });
  const primaryText = item.fileName ?? layoutLabel ?? productCount;
  // Retention: a completed file is downloadable until `expiresAt` (90 days after it finished).
  const expired = item.expired === true;
  const availability = item.expiresAt
    ? expired
      ? t('history.expiredOn', { date: formatDate(item.expiresAt) })
      : t('history.availableUntil', { date: formatDate(item.expiresAt) })
    : null;
  const metaParts = [
    primaryText !== productCount ? productCount : null,
    // The language it was taken in; an older response without the field was English.
    item.locale ?? 'en-US',
    layoutLabel && primaryText !== layoutLabel ? layoutLabel : null,
    createdAt ? formatRelative(createdAt) : null,
  ].filter(Boolean);

  const nameSpan = (
    <span
      ref={nameRef}
      data-testid={`export-history-row-name-${item.exportId}`}
      className="text-foreground truncate text-sm font-medium"
    >
      {primaryText}
    </span>
  );

  return (
    <li
      data-testid={`export-history-row-${item.exportId}`}
      className="flex flex-col gap-2 border-b py-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {/* Format tile — the file type at a glance; falls back to a generic file
            glyph when the format isn't reported. */}
          <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold tracking-wide uppercase">
            {item.format ?? <FileText aria-hidden="true" className="h-4 w-4" />}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            {/* File name is the primary identifier; the full value shows in a
              tooltip only when it's clipped. Falls back to the product count on
              older items that don't report a name. */}
            {isNameTruncated ? (
              <Tooltip>
                <TooltipTrigger asChild>{nameSpan}</TooltipTrigger>
                <TooltipContent className="max-w-xs break-all">{primaryText}</TooltipContent>
              </Tooltip>
            ) : (
              nameSpan
            )}
            {/* Slim status: a coloured dot + label, so it reads light next to the
              file name rather than as a heavy pill. */}
            <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
              <span
                aria-hidden="true"
                className={cn(
                  'h-1.5 w-1.5 shrink-0 rounded-full',
                  statusDotClass(item.status),
                  isActive && 'animate-pulse',
                )}
              />
              <span
                data-testid={`export-history-row-status-${item.exportId}`}
                className="text-foreground font-medium"
              >
                {t(`status.${item.status}`, { defaultValue: item.status })}
              </span>
              {/* Who triggered the export, from the audit stamp (when present). */}
              {item.createdBy && (
                <span className="flex items-center gap-1">
                  <span aria-hidden>·</span>
                  <ActorStringLabel value={item.createdBy} showAvatar={false} className="text-xs" />
                </span>
              )}
            </div>
            {/* Details on their own line, so a wrap never leaves a line starting with "·". */}
            {metaParts.length > 0 && (
              <span className="text-muted-foreground text-xs">{metaParts.join(' · ')}</span>
            )}
            {availability && item.expiresAt && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'w-fit cursor-default text-xs',
                      expired ? 'text-error' : 'text-muted-foreground',
                    )}
                  >
                    {availability}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-72 text-xs">
                  {t(expired ? 'history.expiredHint' : 'history.availableHint', {
                    when: formatDateTime(item.expiresAt),
                  })}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('history.config.toggle')}
                aria-expanded={showConfig}
                data-testid={`export-history-config-toggle-button-${item.exportId}`}
                className={cn('cursor-pointer', showConfig && 'bg-muted')}
                onClick={() => {
                  setShowConfig((v) => !v);
                }}
              >
                <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
                <ChevronDown
                  aria-hidden="true"
                  className={cn('h-3 w-3 transition-transform', showConfig && 'rotate-180')}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('history.config.toggle')}</TooltipContent>
          </Tooltip>
          {item.status === 'COMPLETED' && !expired && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('toast.actions.download')}
              data-testid={`export-history-download-button-${item.exportId}`}
              onClick={() => {
                void download(item.exportId);
              }}
              disabled={isRefreshing}
              className="cursor-pointer"
            >
              {isRefreshing ? (
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
              ) : (
                <Download aria-hidden="true" className="h-4 w-4" />
              )}
            </Button>
          )}
          {isTerminal && (
            // Inline confirm anchored at the trash button — no full-screen modal.
            // Nested inside the sheet's subtree so Radix keeps it a nested layer
            // and interacting with it doesn't dismiss the sheet.
            <ConfirmPopover
              testId={`export-history-delete-confirm-${item.exportId}`}
              open={confirmOpen}
              onOpenChange={setConfirmOpen}
              align="end"
              title={t('history.deleteConfirm.title')}
              description={t('history.deleteConfirm.description')}
              confirmLabel={t('history.deleteConfirm.confirm')}
              cancelLabel={t('history.deleteConfirm.cancel')}
              isPending={deleteMutation.isPending}
              onConfirm={() => {
                deleteMutation.mutate(item.exportId, {
                  // Close on success; the list invalidation then drops this row.
                  // On error the hook toasts and the popover stays open to retry.
                  onSuccess: () => {
                    setConfirmOpen(false);
                  },
                });
              }}
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('history.delete')}
                  data-testid={`export-history-delete-button-${item.exportId}`}
                  className="text-muted-foreground hover:text-destructive cursor-pointer"
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </Button>
              }
            />
          )}
        </div>
      </div>
      {showConfig && (
        <div className="bg-muted/30 ml-12 rounded-md border px-3 py-2.5">
          <ExportConfigDetails item={item} />
        </div>
      )}
    </li>
  );
}

/**
 * Recent-exports panel scoped to a single enrichment run. Lists past exports
 * newest-first (`GET /exports?sourceWorkflowId=`) with per-row re-download
 * (mints a fresh signed URL) and delete (terminal rows only).
 */
export function ExportHistorySheet({
  open,
  onOpenChange,
  sourceWorkflowId,
}: ExportHistorySheetProps) {
  const { t } = useTranslation('exports');
  const [preset, setPreset] = useState<RangePreset>('all');
  const [customFrom, setCustomFrom] = useState<Date | undefined>(undefined);
  const [customTo, setCustomTo] = useState<Date | undefined>(undefined);
  // Resolved when the range is picked (not per render), so "last 7 days" is a stable key.
  const [createdWindow, setCreatedWindow] = useState<ExportCreatedWindow>({});
  const windowed = Boolean(createdWindow.from ?? createdWindow.to);
  const { data, isLoading, isError, refetch } = useExportsList(
    sourceWorkflowId,
    undefined,
    open,
    createdWindow,
  );

  // The list is cached (staleTime), so reopening the sheet would otherwise show
  // a stale snapshot (a finished export missing, a deleted one lingering). Force
  // a fresh fetch each time it opens.
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="export-history-sheet"
        className="flex w-full flex-col gap-0 overflow-hidden sm:max-w-xl"
      >
        <SheetHeader className="space-y-0 border-b pb-4 text-left">
          <div className="flex items-center gap-3">
            <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
              <History aria-hidden="true" className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <SheetTitle className="text-base leading-tight font-semibold">
                {t('history.title')}
              </SheetTitle>
              <SheetDescription className="text-xs">{t('history.description')}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-2 border-b py-3">
          <Select
            value={preset}
            onValueChange={(next) => {
              const picked = next as RangePreset;
              setPreset(picked);
              setCreatedWindow(
                picked === 'custom'
                  ? customWindow(customFrom, customTo)
                  : presetCreatedWindow(picked),
              );
            }}
          >
            <SelectTrigger
              data-testid="export-history-range-select"
              aria-label={t('history.range.label')}
              className="h-8 w-40 cursor-pointer text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-testid="export-history-range-content">
              {RANGE_PRESETS.map((value) => (
                <SelectItem
                  key={value}
                  data-testid={`export-history-range-option-${value}`}
                  value={value}
                  className="cursor-pointer text-sm"
                >
                  {t(`history.range.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === 'custom' && (
            <>
              <DatePicker
                value={customFrom}
                onChange={(date) => {
                  setCustomFrom(date);
                  setCreatedWindow(customWindow(date, customTo));
                }}
                {...(customTo ? { toDate: customTo } : {})}
                placeholder={t('history.range.from')}
                className="h-8 w-36 text-sm"
              />
              <span className="text-muted-foreground text-xs">–</span>
              <DatePicker
                value={customTo}
                onChange={(date) => {
                  setCustomTo(date);
                  setCreatedWindow(customWindow(customFrom, date));
                }}
                {...(customFrom ? { fromDate: customFrom } : {})}
                toDate={new Date()}
                placeholder={t('history.range.to')}
                className="h-8 w-36 text-sm"
              />
            </>
          )}
        </div>

        <div className="mt-4 min-h-0 flex-1 overflow-auto">
          {isLoading && (
            <div data-testid="export-history-loading" className="space-y-3">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={`hist-skel-${String(i)}`} className="h-14 w-full" />
              ))}
            </div>
          )}

          {isError && (
            <p data-testid="export-history-error" className="text-error text-sm">
              {t('history.error')}
            </p>
          )}

          {data?.length === 0 && (
            <p data-testid="export-history-empty" className="text-muted-foreground text-sm">
              {windowed ? t('history.emptyInRange') : t('history.empty')}
            </p>
          )}

          {data && data.length > 0 && (
            <ul data-testid="export-history-list" className="flex flex-col">
              {data.map((item) => (
                <HistoryRow key={item.exportId} item={item} />
              ))}
            </ul>
          )}
          {windowed && data && data.length >= WINDOWED_LIST_LIMIT && (
            <p className="text-muted-foreground mt-2 text-xs">{t('history.range.capped')}</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
